import React, { createContext, useContext, useEffect, useMemo } from "react";

export type Locale = "zh-CN" | "en-US";

type TranslationKey =
  | "slogan"
  | "startUsing"
  | "aiDisclaimer"
  | "footer"
  | "summary"
  | "searchedWebpages"
  | "deepThinking"
  | "modelAvatar"
  | "buttonIcon"
  | "icon";

type TranslationParams = Record<string, number | string>;

interface I18nContextValue {
  t: (key: TranslationKey, params?: TranslationParams) => string;
  dateLocale: string;
}

const translations: Record<Locale, Record<TranslationKey, string>> = {
  "zh-CN": {
    slogan: "让科技触手可及",
    startUsing: "开始使用DeepSurf",
    aiDisclaimer: "AI生成的内容不能完全保障真实",
    footer: "畅享AI带来的超能力",
    summary: "总结",
    searchedWebpages: "已搜索到 {count} 个网页",
    deepThinking: "已深度思考（用时 {seconds} 秒）",
    modelAvatar: "模型头像",
    buttonIcon: "按钮图标",
    icon: "图标",
  },
  "en-US": {
    slogan: "Tech within reach",
    startUsing: "Use DeepSurf",
    aiDisclaimer: "AI-generated content may not be fully accurate",
    footer: "Unlock the power of AI",
    summary: "Summary",
    searchedWebpages: "Found {count} web pages",
    deepThinking: "Deep thinking completed ({seconds}s)",
    modelAvatar: "Model avatar",
    buttonIcon: "Button icon",
    icon: "Icon",
  },
};

function getInitialLocale(): Locale {
  return typeof navigator !== "undefined" && navigator.language === "zh-CN"
    ? "zh-CN"
    : "en-US";
}

const I18nContext = createContext<I18nContextValue | undefined>(undefined);

export const I18nProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const locale = getInitialLocale();

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const value = useMemo<I18nContextValue>(
    () => ({
      dateLocale: locale,
      t: (key, params = {}) => {
        const template = translations[locale][key];
        return Object.entries(params).reduce(
          (result, [name, replacement]) => result.replace(`{${name}}`, String(replacement)),
          template,
        );
      },
    }),
    [locale],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export function useTranslation(): I18nContextValue {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error("useTranslation must be used within I18nProvider");
  }
  return context;
}
